import { type IAnalyserNode, type IAudioContext } from 'standardized-audio-context';
import { v4 as uuid } from 'uuid';
import { type StateCreator } from 'zustand';
import { type AudioGraphStore } from './types';

export interface AnalyzerResult {
  id: string;
  analyzer: IAnalyserNode<IAudioContext>;
}

export interface AnalyzerSlice {
  micAnalyzer: IAnalyserNode<IAudioContext> | undefined;
  tabAnalyzer: IAnalyserNode<IAudioContext> | undefined;
  micAnalyzerUsers: Set<string>;
  tabAnalyzerUsers: Set<string>;
  getMicAnalyzer: () => AnalyzerResult;
  getTabAnalyzer: () => AnalyzerResult;
  releaseMicAnalyzer: (id: string) => void;
  releaseTabAnalyzer: (id: string) => void;
}

export const createAnalyzerSlice: StateCreator<AudioGraphStore, [], [], AnalyzerSlice> = (
  set,
  get
) => ({
  micAnalyzer: undefined,
  tabAnalyzer: undefined,
  micAnalyzerUsers: new Set(),
  tabAnalyzerUsers: new Set(),

  getMicAnalyzer: () => {
    const id = uuid();
    let analyzer = get().micAnalyzer;
    const disconnectAnalyzer = !!analyzer;
    if (!analyzer) analyzer = get().audioContext!.createAnalyser();

    set((state) => {
      return {
        micAnalyzer: analyzer,
        micAnalyzerUsers: new Set(state.micAnalyzerUsers).add(id),
      };
    });

    void get().rebuildGraphTask(disconnectAnalyzer);
    return { id, analyzer };
  },

  getTabAnalyzer: () => {
    const id = uuid();
    let analyzer = get().tabAnalyzer;
    if (!analyzer) analyzer = get().audioContext!.createAnalyser();

    set((state) => {
      return {
        tabAnalyzer: analyzer,
        tabAnalyzerUsers: new Set(state.tabAnalyzerUsers).add(id),
      };
    });

    return { id, analyzer };
  },

  releaseMicAnalyzer: (id) => {
    let analyzer = get().micAnalyzer;
    if (!analyzer) return;
    const { cleanupResourcesIfUnused } = get();

    set((state) => {
      const newAnalyzerUsers = new Set(state.micAnalyzerUsers);
      newAnalyzerUsers.delete(id);
      if (newAnalyzerUsers.size == 0) {
        get().micSourceNode?.disconnect(analyzer!);
        analyzer = undefined;
      }
      return { micAnalyzerUsers: newAnalyzerUsers, micAnalyzer: analyzer };
    });

    cleanupResourcesIfUnused();
  },

  releaseTabAnalyzer: (id) => {
    let analyzer = get().tabAnalyzer;
    if (!analyzer) return;
    const { cleanupResourcesIfUnused } = get();

    set((state) => {
      const newAnalyzerUsers = new Set(state.tabAnalyzerUsers);
      newAnalyzerUsers.delete(id);
      if (newAnalyzerUsers.size == 0) {
        get().tabSourceNode?.disconnect(analyzer!);
        analyzer = undefined;
      }
      return { tabAnalyzerUsers: newAnalyzerUsers, tabAnalyzer: analyzer };
    });

    cleanupResourcesIfUnused();
  },
});
